import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiPost } from "../../lib/api";
import { useTenantAuth } from "../../context/auth";

type TelegramWebAppHandle = {
  initData?: string;
  initDataUnsafe?: { start_param?: string };
};

function getTelegramInitData() {
  const tg = (window as any)?.Telegram?.WebApp as TelegramWebAppHandle | undefined;
  return (tg?.initData as string | undefined) || "";
}

function getLaunchParamFromHash(key: string): string {
  const hash = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
  if (!hash) return "";
  const params = new URLSearchParams(hash);
  return params.get(key) || "";
}

function getTelegramStartParam() {
  const tg = (window as any)?.Telegram?.WebApp as TelegramWebAppHandle | undefined;
  return (
    tg?.initDataUnsafe?.start_param ||
    new URLSearchParams(window.location.search).get("startapp") ||
    new URLSearchParams(window.location.search).get("tgWebAppStartParam") ||
    getLaunchParamFromHash("tgWebAppStartParam") ||
    ""
  );
}

export function TelegramOwnerOnboardPage() {
  const navigate = useNavigate();
  const { loginWithToken } = useTenantAuth();
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get("token") || "");
  const [initData, setInitData] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    const value = getTelegramInitData();
    if (value) setInitData(value);
    if (!token) {
      const startParam = getTelegramStartParam();
      if (startParam && startParam.startsWith("onboard_")) {
        setToken(startParam.slice("onboard_".length));
      }
    }
  }, [token]);

  const redeem = async () => {
    setBusy(true);
    setError("");
    setStatus("");
    try {
      const res = await apiPost<{ access_token: string; tenant_id: string; redirect_path?: string }>(
        "/v3/auth/telegram/onboard",
        { token, init_data: initData },
        { auth: "none" }
      );
      loginWithToken(res.access_token);
      setStatus(`Owner onboarding successful for tenant ${res.tenant_id}`);
      navigate(res.redirect_path || "/app/dashboard", { replace: true });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Onboarding failed";
      if (/init data expired/i.test(msg) || /invalid_initdata/i.test(msg)) {
        setError("Telegram session expired. Re-open the invite from Telegram and redeem first, then connect your own bot later if needed.");
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="mx-auto max-w-2xl panel p-6 grid gap-4">
        <h1 className="text-2xl font-semibold">Owner Onboarding (Sales-Assisted)</h1>
        <p className="text-sm text-slate-600">
          Redeem a single-use onboarding token and sign in without a password. You do not need to create your own Telegram bot now. After success you will be opened in the Telegram dashboard and can upgrade later.
        </p>
        <input className="input" placeholder="Onboarding token" value={token} onChange={(e) => setToken(e.target.value)} />
        <textarea className="input min-h-28" placeholder="Telegram WebApp initData (auto-filled in Telegram Mini App)" value={initData} onChange={(e) => setInitData(e.target.value)} />
        <button className="btn" disabled={busy || !token.trim() || !initData.trim()} onClick={redeem}>{busy ? "Redeeming..." : "Redeem Onboarding Token"}</button>
        {status ? <p className="text-sm text-emerald-700">{status}</p> : null}
        {error ? <p className="text-sm text-rose-600">{error}</p> : null}
      </div>
    </div>
  );
}
